import * as tiptap from '@tiptap/react';
import { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import StarterKit from '@tiptap/starter-kit';
import debounce from 'lodash/debounce';
import { buildTiptapState } from './parse/parseHelper';
import { parseTiptapDocToEditorState } from './print/printHelper';
import { createTextBlockNode } from './extensions/text';
import { createBulletListBlockNode } from './extensions/list';
import { createListItemBlockNode } from './extensions/list-item';
import { createCalloutBlockNode } from './extensions/callout';
import { createHeadingBlockNode } from './extensions/heading';
import { createMentionExtension } from './extensions/mention';
import { createCustomBlockNode } from './extensions/custom';
import { createCustomEnterExtension } from './extensions/enter';
import { createCustomUniqueIdExtension } from './extensions/unique-id';
import { createSuggestionAddMark, createSuggestionDeleteMark, } from './extensions/suggestion';
import { applySuggestionToEditor } from './suggestions/applySuggestions';
const DEFAULT_DEBOUNCE_DELAY = 250;
export function createTiptapEditorController(config) {
    const { context, debounceDelayMs } = config;
    const actualDebounceDelay = debounceDelayMs ?? DEFAULT_DEBOUNCE_DELAY;
    return {
        useProps(props) {
            const { as: Theme, onUpdate: originalOnUpdate, onSelect: originalOnSelect, state: editorCoreState, onMount, } = props;
            // Internal suggestion state
            const [internalSuggestion, setInternalSuggestion] = useState({
                id: null,
                isProcessing: false,
                action: null,
            });
            // Initialize internal suggestion from prop
            useEffect(() => {
                if (props.suggestion) {
                    console.log('TipTap: Initializing internal suggestion from prop');
                    setInternalSuggestion({
                        id: 'props.suggestion.id',
                        isProcessing: false,
                        action: null,
                    });
                }
                else if (!props.suggestion &&
                    internalSuggestion.id &&
                    !internalSuggestion.isProcessing) {
                    console.log('TipTap: Clearing internal suggestion after external suggestion removed');
                    setInternalSuggestion({
                        id: null,
                        isProcessing: false,
                        action: null,
                    });
                }
            }, [
                props.suggestion,
                internalSuggestion.id,
                internalSuggestion.isProcessing,
            ]);
            const latestOnUpdateRef = useRef(originalOnUpdate);
            useEffect(() => {
                latestOnUpdateRef.current = originalOnUpdate;
            }, [originalOnUpdate]);
            const latestOnSuggestionUpdateRef = useRef(props.onSuggestionUpdate);
            useEffect(() => {
                latestOnSuggestionUpdateRef.current = props.onSuggestionUpdate;
            }, [props.onSuggestionUpdate]);
            const callLatestOnUpdate = useCallback((newEditorState) => {
                // Only call onUpdate if we're not processing a suggestion
                if (!internalSuggestion.isProcessing && latestOnUpdateRef.current) {
                    latestOnUpdateRef.current(newEditorState);
                }
                else {
                    console.log('TipTap: Skipping onUpdate during suggestion processing');
                }
            }, [internalSuggestion.isProcessing]);
            const debouncedHandler = useMemo(() => {
                return debounce((editor) => {
                    const tiptapJsonContent = editor.getJSON();
                    const newEditorState = parseTiptapDocToEditorState(tiptapJsonContent);
                    callLatestOnUpdate(newEditorState);
                }, actualDebounceDelay);
            }, [actualDebounceDelay, callLatestOnUpdate]);
            useEffect(() => {
                return () => {
                    debouncedHandler.cancel();
                };
            }, [debouncedHandler]);
            const latestOnSelectRef = useRef(originalOnSelect);
            useEffect(() => {
                latestOnSelectRef.current = originalOnSelect;
            }, [originalOnSelect]);
            const handleMouseUp = useCallback((view, event) => {
                const { state } = view;
                const { selection } = state;
                const { from, to } = selection;
                if (from === to) {
                    if (latestOnSelectRef.current) {
                        latestOnSelectRef.current(null);
                    }
                    return false;
                }
                let plaintext = '';
                const blocks = [];
                const resolvedFrom = state.doc.resolve(from);
                const startLineNumber = resolvedFrom.depth > 0 ? resolvedFrom.index(0) + 1 : 1;
                const startColumn = from - (resolvedFrom.depth > 0 ? resolvedFrom.start(1) : 0);
                const resolvedTo = state.doc.resolve(to);
                const endColumn = to - (resolvedTo.depth > 0 ? resolvedTo.start(1) : 0);
                state.doc.nodesBetween(from, to, (node, pos) => {
                    if (node.isText) {
                        plaintext += node.text?.slice(Math.max(0, from - pos), to - pos);
                    }
                    else if (node.isBlock) {
                        const blockId = node.attrs.id ||
                            node.attrs.blockId ||
                            node.attrs.tiptapNodeId;
                        if (blockId && typeof blockId === 'string') {
                            if (!blocks.find((b) => b.id === blockId)) {
                                blocks.push({ id: blockId });
                            }
                        }
                    }
                });
                const endLineNumber = startLineNumber + (blocks.length > 0 ? blocks.length - 1 : 0);
                const customSelection = {
                    plaintext: plaintext.trim(),
                    blocks,
                    start: {
                        lineNumber: startLineNumber,
                        column: startColumn,
                        offset: from,
                    },
                    end: {
                        lineNumber: endLineNumber,
                        column: endColumn,
                        offset: to,
                    },
                };
                if (latestOnSelectRef.current) {
                    latestOnSelectRef.current(customSelection);
                }
                return false;
            }, []);
            const tiptapInitialState = useMemo(() => {
                return buildTiptapState(editorCoreState);
            }, [editorCoreState]);
            const editorExtensions = useMemo(() => [
                createCustomEnterExtension(),
                StarterKit.configure({
                    codeBlock: false,
                    listItem: false,
                    bulletList: false,
                }),
                createCustomUniqueIdExtension(),
                createTextBlockNode({ Component: Theme.Text, blockContext: context }),
                createHeadingBlockNode({
                    Component: Theme.Heading,
                    blockContext: context,
                }),
                createCalloutBlockNode({
                    Component: Theme.Callout,
                    blockContext: context,
                }),
                createBulletListBlockNode({ Component: Theme.BulletList }),
                createListItemBlockNode({
                    Component: Theme.ListItem,
                    blockContext: context,
                }),
                createMentionExtension({
                    Component: Theme.Mention,
                    blockContext: context,
                }),
                createCustomBlockNode({
                    Component: Theme.Custom,
                    blockContext: context,
                }),
                createSuggestionAddMark({
                    MarkComponent: Theme.Mark,
                }),
                createSuggestionDeleteMark({
                    MarkComponent: Theme.Mark,
                }),
                // Disabled popover extension - using external controls only
                // SuggestionPopoverExtension.configure({
                //     Component: Theme.SuggestionPopover,
                //     suggestion: null,
                // }),
            ], [Theme, context]);
            const memoizedEditorProps = useMemo(() => ({
                attributes: {
                    class: 'focus:outline-none prose prose-invert max-w-none',
                },
                handleDOMEvents: {
                    mouseup: handleMouseUp,
                },
            }), [handleMouseUp]);
            const editor = tiptap.useEditor({
                extensions: editorExtensions,
                content: tiptapInitialState,
                editorProps: memoizedEditorProps,
                onUpdate: ({ editor }) => {
                    debouncedHandler(editor);
                },
            }, [editorExtensions, memoizedEditorProps]);
            useEffect(() => {
                if (editor) {
                    // Don't reset content if we have an active suggestion, are processing one, or have just processed one
                    const hasActiveSuggestion = internalSuggestion.id !== null ||
                        internalSuggestion.isProcessing ||
                        internalSuggestion.action !== null;
                    if (hasActiveSuggestion) {
                        console.log('Skipping editor content update due to active/processed suggestion', {
                            suggestionId: internalSuggestion.id,
                            isProcessing: internalSuggestion.isProcessing,
                            action: internalSuggestion.action,
                        });
                        return;
                    }
                    // Safety check: only update if content is actually different
                    const currentContent = editor.getJSON();
                    const newContent = tiptapInitialState;
                    // Simple comparison to avoid unnecessary updates
                    if (JSON.stringify(currentContent) !== JSON.stringify(newContent)) {
                        console.log('Setting editor content');
                        try {
                            editor.commands.setContent(newContent);
                        }
                        catch (error) {
                            console.error('Error setting editor content:', error);
                        }
                    }
                }
            }, [
                tiptapInitialState,
                editor,
                internalSuggestion.id,
                internalSuggestion.isProcessing,
                internalSuggestion.action,
            ]);
            // Update suggestion decoration when suggestion changes
            // Disabled - using external controls only
            // useEffect(() => {
            //     if (editor && editor.isEditable) {
            //         editor.extensionManager.extensions.forEach((extension) => {
            //             if (extension.name === 'suggestionPopover') {
            //                 extension.options.suggestion = null;
            //                 // Force re-render of decorations
            //                 editor.view.dispatch(editor.state.tr);
            //             }
            //         });
            //     }
            // }, [editor]);
            // Apply initial suggestion when editor is ready and we have a new suggestion
            useEffect(() => {
                if (editor &&
                    editor.isEditable &&
                    props.suggestion &&
                    !internalSuggestion.isProcessing) {
                    console.log('TipTap Controller: Applying initial suggestion');
                    // Apply the suggestion marks
                    try {
                        const originalNodes = new Map();
                        applySuggestionToEditor(editor, props.suggestion, originalNodes);
                        // Update internal state to track this suggestion
                        setInternalSuggestion({
                            id: 'props.suggestion.id',
                            isProcessing: false,
                            action: null,
                        });
                        // Notify that we're processing the suggestion
                        if (latestOnSuggestionUpdateRef.current) {
                            latestOnSuggestionUpdateRef.current({
                                type: 'processing',
                            });
                        }
                    }
                    catch (error) {
                        console.error('Failed to apply initial suggestion:', error);
                    }
                }
            }, [
                editor,
                props.suggestion,
                internalSuggestion.id,
                internalSuggestion.isProcessing,
            ]);
            // Suggestion control methods
            const acceptSuggestion = useCallback((suggestionId) => {
                if (!editor || internalSuggestion.isProcessing)
                    return;
                console.log('TipTap: Accepting suggestion', suggestionId);
                // Mark as processing
                setInternalSuggestion((prev) => ({
                    ...prev,
                    isProcessing: true,
                    action: 'accept',
                }));
                // Remove suggestion marks for accepted suggestion
                const { tr } = editor.state;
                let marksRemoved = false;
                editor.state.doc.descendants((node, nodePos) => {
                    node.marks.forEach((mark) => {
                        // The applySuggestionToEditor uses operation.id for suggestionId
                        // For now, accept all suggestion marks until we can match properly
                        if (mark.type.name === 'suggestionAdd' ||
                            mark.type.name === 'suggestionDelete') {
                            console.log('TipTap: Found suggestion mark to remove');
                            tr.removeMark(nodePos, nodePos + node.nodeSize, mark.type);
                            marksRemoved = true;
                        }
                    });
                });
                if (marksRemoved) {
                    console.log('TipTap: Marks removed, dispatching transaction');
                    editor.view.dispatch(tr);
                }
                // Schedule the final updates for next frame
                requestAnimationFrame(() => {
                    // Get the final editor state
                    const tiptapJsonContent = editor.getJSON();
                    const newEditorState = parseTiptapDocToEditorState(tiptapJsonContent);
                    // Call onUpdate with the final state
                    if (latestOnUpdateRef.current) {
                        latestOnUpdateRef.current(newEditorState);
                    }
                    // Notify that suggestion was accepted
                    if (latestOnSuggestionUpdateRef.current) {
                        latestOnSuggestionUpdateRef.current({
                            type: 'accepted',
                            suggestionId,
                        });
                    }
                    // Only clear processing state, keep the ID until external suggestion is cleared
                    setInternalSuggestion((prev) => ({
                        ...prev,
                        isProcessing: false,
                        action: 'accept',
                    }));
                    console.log('TipTap: Suggestion acceptance complete');
                });
            }, [editor, internalSuggestion.isProcessing]);
            const rejectSuggestion = useCallback((suggestionId) => {
                if (!editor || internalSuggestion.isProcessing)
                    return;
                console.log('TipTap: Rejecting suggestion', suggestionId);
                // Mark as processing
                setInternalSuggestion((prev) => ({
                    ...prev,
                    isProcessing: true,
                    action: 'reject',
                }));
                try {
                    // Use a simple approach: remove all suggestion content and marks
                    const { tr } = editor.state;
                    let changesMade = false;
                    // First pass: collect content to remove (suggestionAdd marks) in reverse order
                    const nodesToRemove = [];
                    const blocksToCheck = [];
                    editor.state.doc.descendants((node, nodePos) => {
                        // Track block nodes that might become empty
                        if (node.isBlock) {
                            blocksToCheck.push({ pos: nodePos, node });
                        }
                        node.marks.forEach((mark) => {
                            if (mark.type.name === 'suggestionAdd') {
                                nodesToRemove.push({
                                    from: nodePos,
                                    to: nodePos + node.nodeSize,
                                });
                            }
                        });
                    });
                    // Remove suggestionAdd content (in reverse order to maintain positions)
                    nodesToRemove
                        .sort((a, b) => b.from - a.from)
                        .forEach(({ from, to }) => {
                        console.log('TipTap: Removing suggestion content from', from, 'to', to);
                        tr.delete(from, to);
                        changesMade = true;
                    });
                    // After removing content, remove any remaining suggestion marks
                    // We need to traverse the document again since positions changed
                    tr.doc.descendants((node, nodePos) => {
                        node.marks.forEach((mark) => {
                            if (mark.type.name === 'suggestionAdd' ||
                                mark.type.name === 'suggestionDelete') {
                                tr.removeMark(nodePos, nodePos + node.nodeSize, mark.type);
                                changesMade = true;
                            }
                        });
                    });
                    // Third pass: remove empty blocks that were left behind
                    const emptyBlocksToRemove = [];
                    tr.doc.descendants((node, nodePos) => {
                        if (node.isBlock) {
                            // Check if the block is empty or only contains whitespace
                            const isEmpty = node.content.size === 0 ||
                                (node.textContent && node.textContent.trim() === '');
                            // Only remove if it's a content block (not structural like list containers)
                            const isContentBlock = node.type.name === 'textBlock' ||
                                node.type.name === 'paragraph' ||
                                node.type.name === 'calloutBlock' ||
                                node.type.name === 'headingBlock';
                            if (isEmpty && isContentBlock) {
                                console.log('TipTap: Found empty block to remove at', nodePos, 'type:', node.type.name);
                                emptyBlocksToRemove.push({
                                    from: nodePos,
                                    to: nodePos + node.nodeSize,
                                });
                            }
                        }
                    });
                    // Remove empty blocks (in reverse order to maintain positions)
                    emptyBlocksToRemove
                        .sort((a, b) => b.from - a.from)
                        .forEach(({ from, to }) => {
                        console.log('TipTap: Removing empty block from', from, 'to', to);
                        tr.delete(from, to);
                        changesMade = true;
                    });
                    if (changesMade) {
                        console.log('TipTap: Suggestion content/marks removed, dispatching transaction');
                        editor.view.dispatch(tr);
                    }
                    // Schedule the final updates for next frame
                    requestAnimationFrame(() => {
                        // Get the final editor state
                        const tiptapJsonContent = editor.getJSON();
                        const newEditorState = parseTiptapDocToEditorState(tiptapJsonContent);
                        // Call onUpdate with the final state
                        if (latestOnUpdateRef.current) {
                            latestOnUpdateRef.current(newEditorState);
                        }
                        // Notify that suggestion was rejected
                        if (latestOnSuggestionUpdateRef.current) {
                            latestOnSuggestionUpdateRef.current({
                                type: 'rejected',
                                suggestionId,
                            });
                        }
                        // Only clear processing state, keep the ID until external suggestion is cleared
                        setInternalSuggestion((prev) => ({
                            ...prev,
                            isProcessing: false,
                            action: 'reject',
                        }));
                        console.log('TipTap: Suggestion rejection complete');
                    });
                }
                catch (error) {
                    console.error('Error rejecting suggestion:', error);
                    // Reset processing state on error
                    setInternalSuggestion((prev) => ({
                        ...prev,
                        isProcessing: false,
                    }));
                }
            }, [editor, internalSuggestion.isProcessing]);
            // Create editor instance and notify when ready
            const editorInstance = useMemo(() => ({
                focus: () => {
                    if (editor) {
                        editor.commands.focus();
                    }
                },
                acceptSuggestion,
                rejectSuggestion,
                replaceContent: (state) => {
                    if (editor) {
                        // Convert EditorState to TipTap format
                        const tiptapContent = buildTiptapState(state);
                        // Clear all content and set new content
                        editor.commands.clearContent();
                        editor.commands.setContent(tiptapContent);
                    }
                },
            }), [editor, acceptSuggestion, rejectSuggestion]);
            useEffect(() => {
                if (editor && onMount) {
                    onMount(editorInstance);
                }
            }, [editor, onMount, editorInstance]);
            return {
                state: editor,
            };
        },
    };
}
//# sourceMappingURL=tiptapEditorController.js.map